import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectWell, setWellQuery } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { useGetWellsQuery } from "@api/mApi";

/**
 * Well switcher — moved out of the (now-removed) top header into the top of the
 * left nav, under the wordmark. Expanded: a full-width button showing the
 * current well. Collapsed: a compact square avatar (the well's initial) that
 * still opens the same popover. The popover lists every well; picking one drives
 * every screen through wellContext.
 */
export function WellSwitcher({ collapsed }: { collapsed: boolean }) {
  const dispatch = useAppDispatch();
  const { t } = useLocale();
  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  const { data: wells = [] } = useGetWellsQuery();

  const current = wells.find((w) => w.id === selectedWellId) ?? wells[0];
  const currentName = current?.commonName ?? "—";
  const initial = currentName.trim().charAt(0).toUpperCase() || "•";

  const [open, setOpen] = useState(false);
  // Shared with the Well Explorer, so typing here filters the Explorer list + map.
  const query = useAppSelector((s) => s.wellContext.wellQuery);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Filter the (potentially thousands of) wells by name or UWI, and cap the
  // rendered list for responsiveness — the search narrows it, so the cap is
  // only hit before the user types.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return wells;
    return wells.filter((w) =>
      `${w.commonName ?? ""} ${w.uwi ?? ""} ${w.apiNo ?? ""} ${w.field ?? ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [wells, query]);
  const MAX_ROWS = 200;
  const shown = filtered.slice(0, MAX_ROWS);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="well-switcher-wrap" ref={rootRef}>
      <button
        type="button"
        className={`well-switcher${collapsed ? " collapsed" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t("topbar.wellSwitcher")}
        title={collapsed ? `${t("topbar.wellSwitcher")}: ${currentName}` : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {collapsed ? (
          <span className="well-avatar" aria-hidden="true">
            {initial}
          </span>
        ) : (
          <>
            <span className="well-avatar" aria-hidden="true">
              {initial}
            </span>
            <span className="well-name">{currentName}</span>
            <span className="chev" aria-hidden="true">
              ▾
            </span>
          </>
        )}
      </button>

      {open && (
        <ul className="well-menu" role="listbox" id={menuId} aria-label={t("well.switcher.title")}>
          <li
            className="well-menu-search"
            style={{ position: "sticky", top: 0, background: "var(--surface, #fff)", zIndex: 1 }}
          >
            <input
              type="text"
              placeholder="Buscar pozo o UWI…"
              aria-label="Buscar pozo"
              value={query}
              onChange={(e) => dispatch(setWellQuery(e.target.value))}
              autoFocus
              style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px" }}
            />
          </li>
          {filtered.length === 0 && <li className="well-menu-empty">{t("common.na")}</li>}
          {shown.map((w) => (
            <li key={w.id} role="option" aria-selected={w.id === current?.id}>
              <button
                type="button"
                className={w.id === current?.id ? "active" : ""}
                onClick={() => {
                  dispatch(selectWell(w.id));
                  dispatch(setWellQuery(w.commonName));
                  setOpen(false);
                }}
              >
                <span className="well-menu-name">{w.commonName}</span>
                <span className="well-menu-uwi">{w.uwi}</span>
              </button>
            </li>
          ))}
          {filtered.length > shown.length && (
            <li className="well-menu-empty">
              +{filtered.length - shown.length} más — seguí escribiendo
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
